import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {Box, Divider, IconButton, InputBase, MenuItem, MenuList, Paper, Popover, Stack, Typography} from '@mui/material';
import {Fragment, forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {PickerKeyboardHints, usePickerNavigation} from '../../../../../common/pickers/PickerNavigation';
import {variableChipSx} from '../../../../../common/variables/variableChipStyles';
import {automationOptionToken, filterAutomationOptions, type AutomationVariableOption} from './automationVariableOptions';
import {automationVariableLabel} from '../../../config/variableReferences';
import {readAutomationVariableReference} from './automationVariableText';

export interface AutomationVariablePickerHandle {
    onKeyDown: (event: KeyboardEvent) => boolean;
}

interface Props {
    options: AutomationVariableOption[];
    onSelect: (option: AutomationVariableOption) => void;
    purpose?: 'insert-placeholder' | 'select-step-value' | 'change-source';
    /** A slash menu is searched from the editor; the standalone picker owns its search input. */
    query?: string;
}

const groupKey = (option: AutomationVariableOption) => option.kind === 'placeholder'
    ? 'placeholder'
    : `reference:${option.variable.nodeName}`;

/** The automation catalogue owns its source/field structure; the authoring pickers share a look. */
const AutomationVariablePicker = forwardRef<AutomationVariablePickerHandle, Props>(
    ({options, onSelect, purpose = 'insert-placeholder', query: hostQuery}, ref) => {
    const {t} = useTranslation();
    const [ownQuery, setQuery] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const [detail, setDetail] = useState<{anchor: HTMLElement; option: AutomationVariableOption} | null>(null);
    const detailSource = detail?.option.kind === 'placeholder' ? readAutomationVariableReference(detail.option.placeholder.value) : null;
    const searchRef = useRef<HTMLInputElement>(null);
    const choosingSource = purpose === 'change-source';
    const kind = purpose === 'insert-placeholder' ? 'placeholder' : 'reference';
    const searchLabel = t('Search variables');
    const query = hostQuery ?? ownQuery;
    const categoryOptions = options.filter(option => option.kind === kind);
    const items = filterAutomationOptions(categoryOptions, query);
    const {selectedIndex, listRef, selectFromPointer, handleKey} = usePickerNavigation({items, onSelect, resetKey: `${kind}:${query}`});
    const handlePickerKey = (event: Pick<KeyboardEvent, 'key' | 'altKey' | 'shiftKey' | 'metaKey' | 'ctrlKey'>) => {
        if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return false;
        return handleKey(event.key);
    };
    useImperativeHandle(ref, () => ({onKeyDown: handlePickerKey}));
    const counts = items.reduce<Record<string, number>>((result, option) => {
        const group = groupKey(option);
        result[group] = (result[group] ?? 0) + 1;
        return result;
    }, {});

    return (
        <Paper
            variant={'outlined'}
            sx={{width: 380, maxWidth: 'calc(100vw - 32px)', borderRadius: 2, overflow: 'hidden'}}
            onKeyDown={event => {
                // Info and clear buttons retain their native Enter/Space behavior.
                if ((event.target as HTMLElement).closest('button')) return;
                if (handlePickerKey(event)) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }}
        >
            {hostQuery === undefined && <>
                <Stack direction={'row'} alignItems={'center'} gap={1}
                       onClick={() => searchRef.current?.focus()}
                       sx={{px: 2, height: 54, cursor: 'text'}}>
                    <SearchIcon fontSize={'small'} sx={{color: searchFocused ? 'text.primary' : 'text.disabled'}}/>
                    <InputBase
                        fullWidth autoFocus inputRef={searchRef}
                        placeholder={searchLabel}
                        inputProps={{'aria-label': searchLabel}}
                        value={query} onChange={event => setQuery(event.target.value)}
                        onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
                        sx={{typography: 'body1', '& input::placeholder': {color: 'text.secondary', opacity: 1}}}
                    />
                    {query && <IconButton size={'small'} aria-label={t('Clear search')} onClick={() => setQuery('')}>
                        <CloseIcon fontSize={'small'}/>
                    </IconButton>}
                </Stack>
                <Divider/>
            </>}
            <MenuList ref={listRef} disablePadding aria-label={kind === 'placeholder' ? t('Variables') : t('Step values')}
                      sx={{maxHeight: 340, overflowY: 'auto'}}>
                {items.map((option, index) => {
                    const isPlaceholder = option.kind === 'placeholder';
                    const name = isPlaceholder ? option.placeholder.key
                        : choosingSource ? option.variable.nodeName : option.variable.name;
                    const source = option.kind === 'placeholder' ? readAutomationVariableReference(option.placeholder.value) : null;
                    const description = option.kind === 'reference' ? option.variable.description
                        : source ? automationVariableLabel(source) : option.placeholder.value || t('Not configured');
                    return (
                        <Fragment key={automationOptionToken(option)}>
                            {!choosingSource && !isPlaceholder && (index === 0 || groupKey(option) !== groupKey(items[index - 1])) && (
                                <Stack component={'li'} direction={'row'} alignItems={'baseline'} gap={0.75}
                                       sx={{px: 2, pt: 1.5, pb: 0.5}}>
                                    <Typography variant={'overline'} color={'text.secondary'} sx={{overflowWrap: 'anywhere'}}>
                                        {option.variable.nodeName}
                                    </Typography>
                                    <Typography variant={'overline'} color={'text.disabled'}>
                                        {counts[groupKey(option)]}
                                    </Typography>
                                </Stack>
                            )}
                            <MenuItem data-index={index} selected={index === selectedIndex}
                                      onMouseMove={event => selectFromPointer(index, event.clientX, event.clientY)}
                                      onMouseDown={event => { if (hostQuery !== undefined) event.preventDefault(); }}
                                      onClick={() => onSelect(option)}
                                      sx={{gap: 1.5, py: 0.5, pr: 0.5,
                                          '&:hover': {backgroundColor: 'transparent'},
                                          '&.Mui-selected, &.Mui-selected:hover': {backgroundColor: 'action.hover'}}}>
                                <Box sx={{minWidth: 108, maxWidth: '70%', flexShrink: 0, whiteSpace: 'normal'}}>
                                    <Box component={'span'} sx={{...variableChipSx, overflowWrap: 'anywhere'}}>{name}</Box>
                                </Box>
                                <Typography variant={'body2'} noWrap sx={{flex: 1, minWidth: 0}} title={description}>
                                    {description}
                                </Typography>
                                <IconButton size={'small'} aria-label={t('About this variable')}
                                            onClick={event => {
                                                event.stopPropagation();
                                                setDetail({anchor: event.currentTarget, option});
                                            }} sx={{flexShrink: 0}}>
                                    <InfoOutlinedIcon fontSize={'small'}/>
                                </IconButton>
                            </MenuItem>
                        </Fragment>
                    );
                })}
                {items.length === 0 && <Box sx={{px: 2, py: 3, textAlign: 'center'}}>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {categoryOptions.length === 0
                            ? kind === 'placeholder' ? t('Add a variable in the Variables panel to insert it here.') : t('Connect an earlier step to use its values here.')
                            : t('No variable matches "{{query}}"', {query: query.trim()})}
                    </Typography>
                </Box>}
            </MenuList>
            <PickerKeyboardHints/>
            <Popover open={!!detail} anchorEl={detail?.anchor} onClose={() => setDetail(null)}
                     anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                     transformOrigin={{vertical: 'top', horizontal: 'right'}} transitionDuration={0}
                     sx={theme => ({zIndex: theme.zIndex.modal + 2})}
                     slotProps={{paper: {sx: {mt: 0.5, maxWidth: 320, borderRadius: 2}}}}>
                {detail && <Stack gap={1} sx={{p: 2}}>
                    <Box><Box component={'span'} sx={variableChipSx}>
                        {detail.option.kind === 'placeholder' ? detail.option.placeholder.key : detail.option.variable.name}
                    </Box></Box>
                    {detail.option.kind === 'reference' ? <>
                        <Typography variant={'body2'}>{detail.option.variable.description}</Typography>
                        <Typography variant={'body2'} color={'text.secondary'}>{detail.option.variable.nodeName}</Typography>
                    </> : <>
                        <Typography variant={'body2'} color={'text.secondary'}>{t('Step value')}</Typography>
                        <Typography variant={'body2'} sx={{overflowWrap: 'anywhere'}}>
                            {detailSource ? automationVariableLabel(detailSource) : detail.option.placeholder.value || t('Not configured')}
                        </Typography>
                    </>}
                </Stack>}
            </Popover>
        </Paper>
    );
});

AutomationVariablePicker.displayName = 'AutomationVariablePicker';
export default AutomationVariablePicker;
